<template>
  <div>
    <form @submit.prevent="submit">
      <div class="mt-10 mx-auto max-w-2xl w-full">
        <div class="flex flex-col sm:flex-row items-end">
          <div class="flex-1 w-full mb-4 sm:w-auto sm:mb-0 sm:mr-6">
            <label class="block text-gray-400 mb-2">{{ __('Client') }}</label>
            <multiselect v-model="form.client" label="name" track-by="id" @input="clientSelected" @search-change="search" placeholder="Select" :show-labels="false" :options="clients.data"></multiselect>
            <div v-if="error('client_id', 'job.' + job.id)" class="text-red-500 text-sm mt-2">{{ error('client_id', 'job.' + job.id) }}</div>
          </div>
          <div class="w-full sm:w-56">
            <inertia-link :disabled="!job.permissions.update" @click="" :href="route('clients.create', {job: job.id, registrationNumber: quickJobRegistrationNumber})" class="inline-block text-center text-lg px-4 py-2 rounded-lg text-coral-400 border-2 border-coral-400 hover:bg-coral-400 hover:text-white">
              {{ __('Add new client') }}
            </inertia-link>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row items-end mt-6">
          <div class="flex-1 w-full mb-4 sm:w-auto sm:mb-0 sm:mr-6">
            <label class="block text-gray-400 mb-2">{{ __('Car') }}</label>
            <multiselect :disabled="!form.client" v-model="form.car" label="label" :show-labels="false" placeholder="Select" :options="cars"></multiselect>
            <div v-if="error('car_id', 'job.' + job.id)" class="text-red-500 text-sm mt-2">{{ error('car_id', 'job.' + job.id) }}</div>
          </div>
          <div class="w-full sm:w-56">
            <inertia-link :disabled="!job.permissions.update" v-if="form.client" :href="route('clients.edit', {client: form.client.id, target: 'cars',job: this.job.id})" class="inline-block text-center text-lg px-4 py-2 rounded-lg text-coral-400 border-2 border-coral-400 hover:bg-coral-400 hover:text-white">
              {{ __('Add new car') }}
            </inertia-link>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row mt-6 items-end" v-if="authUser().isAdmin()">
          <div class="flex-1 w-full sm:w-auto mb-4 sm:mb-0 sm:mr-6">
            <label class="block text-gray-400 mb-2">{{ __('Consultant') }}</label>
            <multiselect v-model="form.consultant" label="name" track-by="id" placeholder="Select" :show-labels="false" @search-change="search" :options="consultants.data"></multiselect>
            <div v-if="error('consultant_id', 'job')" class="mt-2 text-coral-400">{{ error('consultant_id', 'job') }}</div>
          </div>
          <div class="w-full sm:w-56"></div>
        </div>

        <div class="flex flex-col sm:flex-row items-end mt-6">
          <div class="flex-1 w-full mb-4 sm:w-auto sm:mb-0 sm:mr-6">
            <label class="block text-gray-400 mb-2">{{ __('Mecanic') }}</label>
            <multiselect v-model="form.technician" label="name" track-by="id" placeholder="Select" :show-labels="false" @search-change="search" :options="technicians.data"></multiselect>
            <div v-if="error('technician_id', 'job.' + job.id)" class="text-red-500 text-sm mt-2">{{ error('technician_id', 'job.' + job.id) }}</div>
          </div>
          <div class="w-full sm:w-56">
            <div class="flex items-center leading-tight">
              <input :disabled="!job.permissions.update" v-model="loadTechniciansFromOtherDealerships" id="load-technicians-from-other-dealerships" ref="checkbox" class="border focus:ring focus:border-coral-400 mr-2" type="checkbox" @change="loadTechnicians()"/>
              <label :disabled="!job.permissions.update" class="block text-gray-900 text-sm font-semibold" for="load-technicians-from-other-dealerships">
                {{ __('Select technician from other dealerships') }}
              </label>
            </div>
          </div>
        </div>

        <div class="flex flex-col sm:flex-row mt-6">
          <div class="flex-1 w-full sm:w-auto sm:mr-6">
            <textarea-input v-model="form.description" :data-error="error('description', 'job.' + job.id)" class="w-full" :label="__('Description')" rows="10"/>
          </div>
          <div class="w-auto sm:w-56"></div>
        </div>

        <div class="flex flex-col sm:flex-row mt-6">
          <div class="flex-1 w-full sm:w-auto sm:mr-6">
            <text-input v-model="form.external_id" :data-error="error('external_id', 'job.' + job.id)" class="w-full" :label="__('Job number')"/>
          </div>
          <div class="w-auto sm:w-56"></div>
        </div>

        <div class="flex flex-col sm:flex-row mt-6">
          <div class="flex-1 w-full sm:w-auto sm:mr-6">
            <div class="flex flex-col sm:flex-row justify-end">
              <loading-button :disabled="!job.permissions.update" type="submit" :loading="submittingForm" class="justify-center text-xl px-4 min-w-40 py-2 rounded text-white bg-coral-400 hover:bg-coral-300">
                {{ __('Save') }}
              </loading-button>
            </div>
          </div>
          <div class="w-auto sm:w-56"></div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import Layout from '@/Shared/Layout';
import Icon from '@/Shared/Icon';
import TextareaInput from '@/Shared/TextareaInput';
import TextInput from '@/Shared/TextInput';
import LoadingButton from '@/Shared/LoadingButton';
import Multiselect from 'vue-multiselect';
import _ from 'lodash';

export default {
  props: {
    clients: Object,
    technicians: Object,
    consultants: Object,
    cars: Array,
    job: Object,
  },
  components: {
    Layout,
    LoadingButton,
    TextareaInput,
    TextInput,
    Icon,
    Multiselect,
  },
  data() {
    return {
      datePickerLocale: window.datePickerLocale,
      submittingForm: false,
      loadTechniciansFromOtherDealerships: false,
      form: {
        client: {},
        car: {},
        consultant: {},
        technician: {},
        description: null,
        external_id: '',
        ...this.job,
      },
    };
  },
  mounted() {
    if (this.$page.props.errors.hasOwnProperty(`job.${this.job.id}`)) {
      this.form = { ...this.$page.props.oldInput };
    }
  },
  methods: {
    submit() {
      this.submittingForm = true;

      this.$inertia.put(this.route('jobs.update', { job: this.job.id }), {
        client_id: this.form.client ? this.form.client.id : null,
        car_id: this.form.car ? this.form.car.id : null,
        technician_id: this.form.technician ? this.form.technician.id : null,
        consultant_id: this.form.consultant ? this.form.consultant.id : null,
        description: this.form.description,
        external_id: this.form.external_id,
      }, {
        preserveScroll: true,
        preserveState: true,
      }).then(() => {
        this.submittingForm = false;
      });
    },
    loadTechnicians() {
      this.form.technician = null;
      this.$inertia.get(this.route('jobs.edit', { job: this.job.id, 'loadTechniciansFromOtherDealerships': this.loadTechniciansFromOtherDealerships }),{}, {
        preserveScroll: true,
        preserveState: true,
        replace: true,
      });
    },
    clientSelected() {
      this.form.car = null;
      this.$nextTick(() => {
        this.$inertia.get(this.route('jobs.edit', { job: this.job.id, 'client_id': this.form.client ? this.form.client.id : null }), {}, {
          preserveScroll: true,
          preserveState: true,
          replace: true,
        });
      });
    },
    search: _.throttle(function (query) {
      this.$inertia.get(this.route('jobs.edit', { job: this.job.id, 'term': query }), {}, {
        preserveScroll: true,
        preserveState: true,
        replace: true,
      });
    }, 150),
  },
  computed: {
    quickJobRegistrationNumber() {
      if (!this.job.hasOwnProperty('meta') || !this.job.meta || !this.job.meta.hasOwnProperty('registration_number')) {
        return null;
      }

      return this.job.meta.registration_number;
    },
  },
};
</script>
